import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api from "../constants/api";
import { useAuth } from "../components/ui/AuthContext.jsx";

const Ctx = createContext(null);
const LEGACY_CACHE_KEY = "wrtk_prefs_cache";
const DEBOUNCE_MS = 400;

function cacheKeyFor(userId) {
  return userId ? `wrtk_prefs_cache_${userId}` : LEGACY_CACHE_KEY;
}

function readCache(userId) {
  try {
    const raw = localStorage.getItem(cacheKeyFor(userId));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeCache(data, userId) {
  if (!userId) return;
  try {
    localStorage.setItem(cacheKeyFor(userId), JSON.stringify(data));
  } catch { /* quota exceeded */ }
}

function hasMaterialPrefs(obj) {
  const m = obj?.material;
  if (!m || typeof m !== "object") return false;
  return Boolean(
    (Array.isArray(m.favorites) && m.favorites.length > 0) ||
    (Array.isArray(m.recents) && m.recents.length > 0)
  );
}

/** Material aus lokalem Cache retten, falls API (noch) leer — z. B. nach fehlgeschlagenem PATCH. */
function recoverMaterialPrefs(apiData, userId) {
  if (hasMaterialPrefs(apiData)) return apiData;

  const cached = readCache(userId);
  if (hasMaterialPrefs(cached)) {
    return { ...apiData, material: cached.material };
  }

  try {
    const raw = localStorage.getItem(LEGACY_CACHE_KEY);
    if (!raw) return apiData;
    const legacy = JSON.parse(raw);
    if (!hasMaterialPrefs(legacy)) return apiData;
    return { ...apiData, material: legacy.material };
  } catch {
    return apiData;
  }
}

function getByPath(obj, path) {
  if (!obj || !path) return undefined;
  const parts = path.split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = cur[p];
  }
  return cur;
}

function setByPath(obj, path, value) {
  const parts = path.split(".");
  const out = structuredClone(obj || {});
  let cur = out;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    if (cur[p] == null || typeof cur[p] !== "object" || Array.isArray(cur[p])) {
      cur[p] = {};
    }
    cur = cur[p];
  }
  cur[parts[parts.length - 1]] = value;
  return out;
}

function topLevelPatch(fullData, path) {
  const topKey = path.split(".")[0];
  return { [topKey]: fullData[topKey] };
}

const MIGRATED_FLAG = "wrtk_prefs_migrated";

function migrateFromLocalStorage(userId) {
  try {
    if (localStorage.getItem(MIGRATED_FLAG)) return null;
    const out = { favorites: {}, views: {}, tabs: {}, sort: {}, filters: {} };
    let found = false;

    // Favorites
    const FAV_TYPES = [
      "service", "projekt_elektro", "projekt_pv", "projekt_sicherheit",
      "projekt_ladestation", "projekt_intern", "projekt_ladenbau",
      "offerte", "maengel", "pendenz",
    ];
    for (const t of FAV_TYPES) {
      const raw = localStorage.getItem(`wrtk_fav_${t}_${userId}`);
      if (raw) {
        try { out.favorites[t] = JSON.parse(raw); found = true; } catch {}
      }
    }

    // Views
    const kv = localStorage.getItem(`wrtk_kalender_view_${userId}`);
    if (kv) { out.views.kalender = kv; found = true; }
    const rs = localStorage.getItem(`wrtk_rapport_scope_${userId}`);
    if (rs) { out.views.rapport_scope = rs; found = true; }

    // MeineAufgaben
    const uid = userId || "anon";
    const mt = localStorage.getItem(`me_tasks_tab_v1_${uid}`);
    if (mt) { out.tabs.meine_aufgaben = mt; found = true; }
    const ms = localStorage.getItem(`me_tasks_sort_v1_${uid}`);
    if (ms) { try { out.sort.meine_aufgaben = JSON.parse(ms); found = true; } catch {} }
    const mst = localStorage.getItem(`me_tasks_status_v1_${uid}`);
    if (mst) { out.filters.meine_aufgaben_status = mst; found = true; }
    const msc = localStorage.getItem(`me_tasks_scope_v1_${uid}`);
    if (msc) { out.filters.meine_aufgaben_scope = msc; found = true; }

    // Pendenzen
    const pt = localStorage.getItem(`pendenzen_tab_${userId || "me"}`);
    if (pt) { out.tabs.pendenzen = pt; found = true; }
    const ps = localStorage.getItem(`pendenzen_sort_${userId || "me"}`);
    if (ps) { try { out.sort.pendenzen = JSON.parse(ps); found = true; } catch {} }

    // Inventar
    const it = localStorage.getItem(`inv_tab_v1_${uid}`);
    if (it) { out.tabs.inventar = it; found = true; }
    const is = localStorage.getItem(`inv_sort_v1_${uid}`);
    if (is) { try { out.sort.inventar = JSON.parse(is); found = true; } catch {} }
    const ifl = localStorage.getItem(`inv_filters_v1_${uid}`);
    if (ifl) { try { out.filters.inventar = JSON.parse(ifl); found = true; } catch {} }

    if (found) {
      // Clean empty sub-objects
      for (const k of Object.keys(out)) {
        if (typeof out[k] === "object" && Object.keys(out[k]).length === 0) delete out[k];
      }
      localStorage.setItem(MIGRATED_FLAG, "1");
      return out;
    }
  } catch {}
  return null;
}

export function PreferencesProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.id;
  const [data, setData] = useState({});
  const [loaded, setLoaded] = useState(false);
  const pendingRef = useRef(null);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  // Load from API on login
  useEffect(() => {
    if (!userId) {
      setData({});
      setLoaded(false);
      return;
    }
    let cancelled = false;
    setData(readCache(userId));
    (async () => {
      try {
        const res = await api.get("/profil/preferences", { silent: true });
        if (cancelled || !mountedRef.current) return;
        const apiData = res.data?.data || {};
        let d = apiData;

        // Auto-migrate from old localStorage keys if DB is empty
        if (Object.keys(d).length === 0) {
          const migrated = migrateFromLocalStorage(userId);
          if (migrated) {
            d = migrated;
            api.patch("/profil/preferences", migrated, { silent: true }).catch(() => {});
          }
        }

        d = recoverMaterialPrefs(d, userId);
        if (hasMaterialPrefs(d) && !hasMaterialPrefs(apiData)) {
          api.patch("/profil/preferences", { material: d.material }, { silent: true }).catch(() => {});
        }

        setData(d);
        writeCache(d, userId);
        setLoaded(true);
      } catch {
        if (cancelled) return;
        setData(readCache(userId));
        setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Debounced flush
  const flush = useCallback(async () => {
    if (!userId || !pendingRef.current) return;
    const patch = pendingRef.current;
    pendingRef.current = null;
    try {
      await api.patch("/profil/preferences", patch, { silent: true });
    } catch (err) {
      console.error("Preferences save failed:", err.message);
    }
  }, [userId]);

  const scheduleSave = useCallback(
    (patchObj) => {
      pendingRef.current = pendingRef.current
        ? { ...pendingRef.current, ...patchObj }
        : patchObj;
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, DEBOUNCE_MS);
    },
    [flush],
  );

  // Flush on unmount / page hide
  useEffect(() => {
    const onHide = () => { if (pendingRef.current) flush(); };
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") onHide();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("beforeunload", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("beforeunload", onHide);
      clearTimeout(timerRef.current);
      if (pendingRef.current) flush();
    };
  }, [flush]);

  const getPref = useCallback((path, fallback) => {
    const v = getByPath(data, path);
    return v !== undefined ? v : fallback;
  }, [data]);

  const setPref = useCallback(
    (path, value) => {
      setData((prev) => {
        const next = setByPath(prev, path, value);
        writeCache(next, userId);
        scheduleSave(topLevelPatch(next, path));
        return next;
      });
    },
    [scheduleSave, userId],
  );

  const ctx = useMemo(
    () => ({ data, loaded, getPref, setPref }),
    [data, loaded, getPref, setPref],
  );

  return <Ctx.Provider value={ctx}>{children}</Ctx.Provider>;
}

export function usePreferences() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("usePreferences must be used within PreferencesProvider");
  return ctx;
}
